import { Helmet } from '@vuer-ai/react-helmet-async';
import { LegalPageLayout } from './LegalPageLayout';
import {
  COMMUNITY_ENFORCEMENT,
  COMMUNITY_LICENSE_URL,
  COMMUNITY_WARNING,
  COPYRIGHT_EMAIL,
  COPYRIGHT_CONTACT_URL,
} from '@/common/components/blueprints/communityPolicy';

export default function CommunityGuidelinesPage() {
  const operator = String(
    import.meta.env.VITE_LEGAL_OPERATOR_NAME || ''
  ).trim();
  const country = String(
    import.meta.env.VITE_LEGAL_OPERATOR_COUNTRY || ''
  ).trim();
  // Publish registered agent details only after the operator configures them.
  const agent = String(import.meta.env.VITE_DMCA_AGENT_CONTACT || '').trim();
  return (
    <>
      <Helmet>
        <title>Community sharing & copyright — Studio Vault</title>
      </Helmet>
      <LegalPageLayout
        eyebrow="COMMUNITY GUIDELINES"
        title="Built together. Shared with permission."
        dateLabel="COMMUNITY POLICY · OCTOBER 13, 2026"
        summary="Studio Vault’s community library is meant to be an open source library of community-driven tools: a place to create, collaborate, learn, and build on one another’s work."
      >
        <section>
          <h2>Respect the people behind the sound</h2>
          <p>
            <strong>{COMMUNITY_WARNING}</strong>
          </p>
          <p>
            {COMMUNITY_ENFORCEMENT} Repeat infringers are subject to termination
            of their community-sharing privileges. Do not evade restrictions
            with another account.
          </p>
          <p>
            This rule covers presets, blueprints, sample packs, wavetables,
            impulse responses, factory content, artwork, and audio previews.
            Giving something away, changing its name, or making a small edit
            does not give you redistribution rights.
          </p>
          <p>
            You can share an original creation you made using paid software. Do
            not bundle the software, its factory content, or third-party
            material whose terms prevent this sharing. List required software
            and editions so others can use their own licensed copies.
          </p>
        </section>
        <section id="license">
          <h2>What you agree to when sharing</h2>
          <p>
            Only publish material you created and control, or material you have
            verified is already in the public domain or under CC0. You must have
            authority to apply CC0 to all your contributions, including attached
            artwork and audio. Other licenses, including attribution or
            share-alike licenses, must not be stripped from someone else’s work.
          </p>
          <p>
            Before publication, you explicitly apply{' '}
            <a href={COMMUNITY_LICENSE_URL} target="_blank" rel="noreferrer">
              CC0 1.0 Universal
            </a>{' '}
            to your contribution. To the extent allowed by law, you waive
            copyright and related rights; CC0 includes a fallback license where
            the waiver is ineffective. Others may copy, change, fork,
            redistribute, and use it commercially without asking permission or
            paying you. CC0 does not require attribution or require derivatives
            to stay open. We encourage giving credit and contributing
            improvements.
          </p>
          <p>
            This dedication is irrevocable to the extent permitted by law.
            Deleting an upload does not withdraw rights already granted. CC0
            does not clear another person’s rights, trademarks, patents,
            privacy, or publicity rights. Do not imply an original creator
            endorses a fork.
          </p>
          <p>
            Existing uploads require their creator’s explicit consent before
            sharing under these terms. Private projects and Studio Vault’s own
            application are not placed under CC0 by this policy. Community
            downloads remain subject to applicable law and the rights the
            uploader actually had.
          </p>
        </section>
        <section id="copyright">
          <h2>Report copyright infringement or illegal content</h2>
          <p>
            No account is required. Email{' '}
            <a href={COPYRIGHT_CONTACT_URL}>{COPYRIGHT_EMAIL}</a> with the
            blueprint URL or ID, an explanation of the problem, supporting links
            or evidence, your name and contact information, and a good-faith
            statement that your report is accurate. Specify the country and
            legal basis where relevant. Signed-in users can also choose “Report
            a problem” on a blueprint.
          </p>
          <p>For a U.S. copyright notice, include:</p>
          <ul>
            <li>
              Your physical or electronic signature and identification of the
              copyrighted work, or a representative list of works.
            </li>
            <li>
              Identification and the precise location of the allegedly
              infringing material, with enough detail for us to find it.
            </li>
            <li>Your address, telephone number, and email address.</li>
            <li>
              A statement that you believe in good faith the use is not
              authorized by the copyright owner, their agent, or the law.
            </li>
            <li>
              A statement that the information is accurate and, under penalty of
              perjury, you are authorized to act for the owner of the allegedly
              infringed exclusive right.
            </li>
          </ul>
          {agent && (
            <p style={{ whiteSpace: 'pre-line' }}>
              Designated copyright agent contact: {agent}
            </p>
          )}
          <p>
            We review notices and may restrict access while investigating.
            Reports do not automatically prove infringement. We consider the
            evidence, applicable exceptions, and the creator’s response.
            Knowingly false reports can cause harm and may have legal
            consequences.
          </p>
        </section>
        <section id="appeals">
          <h2>Decisions, appeals, and counter-notices</h2>
          <p>
            Sharing restrictions and reasons appear in the library and on your
            uploads. Use “Appeal decision” on your blueprint or email{' '}
            <a
              href={`mailto:${COPYRIGHT_EMAIL}?subject=Community%20sharing%20appeal`}
            >
              {COPYRIGHT_EMAIL}
            </a>{' '}
            with the blueprint or account ID, the decision you contest, and
            supporting evidence. A community appeal is not automatically a legal
            counter-notice.
          </p>
          <p>
            If a U.S. copyright removal was based on a mistake or
            misidentification, a counter-notice must include your signature;
            identification and former location of the material; a statement
            under penalty of perjury that you believe in good faith the removal
            was mistaken; and your name, address, and telephone number. It must
            also state your consent to the jurisdiction of the U.S. federal
            district court for your address, or, if outside the U.S., a district
            where the service provider may be found, and acceptance of service
            from the complainant or their agent.
          </p>
          <p>
            When that process applies, a valid counter-notice is forwarded to
            the complainant. Restoration follows the applicable 10–14
            business-day process unless notice of the required court action is
            received. Other independent restrictions may still apply. We do not
            automatically restore disputed content just because an appeal was
            submitted.
          </p>
          <p>
            Contact details and relevant notice information may be shared with
            the affected parties as required to handle the dispute. Avoid
            unnecessary sensitive information. Reports, consent records, file
            hashes, and decision history may be retained for dispute resolution,
            abuse prevention, or legal requirements; see our{' '}
            <a href="/privacy">Privacy Policy</a>.
          </p>
        </section>
        <section>
          <h2>A global community</h2>
          <p>
            We welcome creators around the world, subject to applicable law.
            These rules do not remove mandatory rights or lawful avenues for
            redress in your country. Availability does not override local
            requirements.
          </p>
          {operator && (
            <p>
              Operator: {operator}
              {country ? ` (${country})` : ''}.
            </p>
          )}
          <p>
            The <a href="/terms">Terms of Service</a> also apply. Community
            files are provided as-is without a guarantee of ownership,
            clearance, or compatibility. Stock-device labels describe the
            uploader’s assessment, not a copyright or license verification.
          </p>
        </section>
      </LegalPageLayout>
    </>
  );
}
